import { Box, MenuItem, Typography } from '@mui/material';
import React from 'react';
import { RHFSelect } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import {
  StockBatchView,
  formatDateOnly,
} from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { SPAN_FULL } from './medicationFormLayout';
import { MedicationStockView, PinnedBatchView } from './medicationStockView';
import { selectTestId } from './selectTestId';

interface MedicationStockBatchFieldProps {
  view: MedicationStockView<StockBatchView>;
  batchId: string;
  locked: boolean;
  /** A quantity in the Product's own Unit ("5 ampułek"). */
  inUnits: (milli: number) => string;
}

const batchLabel = (batch: StockBatchView, quantity: string): string =>
  translateKey('warehouse.clinical.batchOption', {
    batch: batch.batchNumber,
    expiry: formatDateOnly(batch.expiryDate),
    quantity,
  });

/** A stored pin FEFO would not offer is still listed, marked (notes-2 F-3). */
function pinnedLabel(
  pinned: PinnedBatchView<StockBatchView>,
  inUnits: (milli: number) => string,
): string {
  if (!pinned.batch) {
    return translateKey('warehouse.clinical.batchOptionMissing');
  }
  if (!pinned.stale) {
    return batchLabel(pinned.batch, inUnits(pinned.onHandMilli));
  }
  return translateKey(
    pinned.stale === 'EXPIRED'
      ? 'warehouse.clinical.batchOptionExpired'
      : 'warehouse.clinical.batchOptionEmpty',
    {
      batch: pinned.batch.batchNumber,
      expiry: formatDateOnly(pinned.batch.expiryDate),
    },
  );
}

/**
 * "Partia": automatic (FEFO, previewed) or a pinned batch in stock. A stored
 * pin that has since expired or emptied stays visible and marked, so the vet
 * sees why a correction may be refused and can switch to "Automatycznie".
 */
export default function MedicationStockBatchField({
  view,
  batchId,
  locked,
  inUnits,
}: MedicationStockBatchFieldProps): React.ReactElement {
  const { usable, pinned } = view;
  const fefo = usable[0];

  return (
    <Box sx={SPAN_FULL}>
      <RHFSelect
        name="stockBatchId"
        label={translateKey('warehouse.clinical.batch')}
        disabled={locked}
        SelectProps={{
          native: false,
          SelectDisplayProps: selectTestId('medication-stock-batch'),
        }}
      >
        <MenuItem value="">
          {translateKey('warehouse.clinical.batchAuto')}
        </MenuItem>
        {usable.map((batch) => (
          <MenuItem key={batch.id} value={batch.id}>
            {batchLabel(batch, inUnits(batch.onHandMilli))}
          </MenuItem>
        ))}
        {pinned && !pinned.listed && (
          <MenuItem key={pinned.id} value={pinned.id}>
            {pinnedLabel(pinned, inUnits)}
          </MenuItem>
        )}
      </RHFSelect>
      {!batchId && (
        <Typography
          variant="caption"
          display="block"
          color="text.secondary"
          sx={{ mt: 0.5 }}
          data-testid="medication-stock-batch-preview"
        >
          {fefo
            ? translateKey('warehouse.clinical.batchAutoPreview', {
                batch: fefo.batchNumber,
                expiry: formatDateOnly(fefo.expiryDate),
              })
            : translateKey('warehouse.clinical.batchNoneAvailable')}
        </Typography>
      )}
      {pinned?.stale && (
        <Typography
          variant="caption"
          display="block"
          color="warning.main"
          sx={{ mt: 0.5 }}
          data-testid="medication-stock-batch-stale"
        >
          {translateKey('warehouse.clinical.batchPinUnusable')}
        </Typography>
      )}
    </Box>
  );
}
